/**
 * Universal Bookmark Component
 * Works across all content types with consistent UI/UX
 */

'use client';

import { useState, useEffect } from 'react';
import { Bookmark, BookmarkCheck, Loader2 } from 'lucide-react';
import { useAuth } from '@/lib/auth/AuthContext';

export type ContentType =
  | 'insight'
  | 'framework'
  | 'tool'
  | 'comparison'
  | 'code'
  | 'architecture'
  | 'template'
  | 'question'
  | 'learning-path';

interface UniversalBookmarkProps {
  contentType: ContentType;
  contentSlug: string;
  contentTitle?: string;
  variant?: 'icon' | 'button' | 'compact';
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  onBookmarkChange?: (isBookmarked: boolean) => void;
}

export default function UniversalBookmark({
  contentType,
  contentSlug,
  contentTitle,
  variant = 'icon',
  size = 'md',
  className = '',
  onBookmarkChange,
}: UniversalBookmarkProps) {
  const { isAuthenticated, login } = useAuth();
  const [isBookmarked, setIsBookmarked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isChecking, setIsChecking] = useState(true);

  // Check if content is already bookmarked
  useEffect(() => {
    if (!isAuthenticated) {
      setIsChecking(false);
      return;
    }

    const checkBookmark = async () => {
      try {
        const response = await fetch(
          `/api/secure/saved-content?type=${contentType}&slug=${contentSlug}`,
          { credentials: 'include' }
        );
        if (response.ok) {
          const data = await response.json();
          setIsBookmarked(data.data?.items?.length > 0);
        }
      } catch (error) {
        console.error('Failed to check bookmark status:', error);
      } finally {
        setIsChecking(false);
      }
    };

    checkBookmark();
  }, [isAuthenticated, contentType, contentSlug]);

  const handleToggle = async () => {
    if (!isAuthenticated) {
      login?.({ returnTo: window.location.pathname });
      return;
    }

    setIsLoading(true);

    try {
      if (isBookmarked) {
        // Remove bookmark
        const response = await fetch('/api/secure/saved-content', {
          method: 'DELETE',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            contentType,
            contentSlug,
          }),
        });

        if (response.ok) {
          setIsBookmarked(false);
          onBookmarkChange?.(false);
        }
      } else {
        // Add bookmark
        const response = await fetch('/api/secure/saved-content', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            contentType,
            contentSlug,
            title: contentTitle,
          }),
        });

        if (response.ok) {
          setIsBookmarked(true);
          onBookmarkChange?.(true);
        }
      }
    } catch (error) {
      console.error('Failed to toggle bookmark:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
  };

  const buttonSizeClasses = {
    sm: 'px-2 py-1 text-xs',
    md: 'px-3 py-1.5 text-sm',
    lg: 'px-4 py-2 text-base',
  };

  if (isChecking) {
    return (
      <div className={`text-foreground-muted ${className}`}>
        <Loader2 className={`${sizeClasses[size]} animate-spin`} />
      </div>
    );
  }

  // Icon-only variant
  if (variant === 'icon') {
    return (
      <button
        onClick={handleToggle}
        disabled={isLoading}
        className={`
          group relative p-2 rounded-lg transition-all
          ${isBookmarked ? 'text-accent' : 'text-foreground-tertiary hover:text-foreground'}
          hover:bg-cream/10 disabled:opacity-50
          ${className}
        `}
        title={isBookmarked ? 'Remove bookmark' : 'Add bookmark'}
      >
        {isLoading ? (
          <Loader2 className={`${sizeClasses[size]} animate-spin`} />
        ) : isBookmarked ? (
          <BookmarkCheck className={sizeClasses[size]} />
        ) : (
          <Bookmark className={sizeClasses[size]} />
        )}
      </button>
    );
  }

  // Compact variant (icon with label on hover)
  if (variant === 'compact') {
    return (
      <button
        onClick={handleToggle}
        disabled={isLoading}
        className={`
          group flex items-center gap-1.5 px-2 py-1 rounded-lg transition-all
          ${isBookmarked ? 'bg-accent-muted text-accent' : 'text-foreground-tertiary hover:text-foreground hover:bg-cream/10'}
          disabled:opacity-50
          ${className}
        `}
      >
        {isLoading ? (
          <Loader2 className={`${sizeClasses[size]} animate-spin`} />
        ) : isBookmarked ? (
          <BookmarkCheck className={sizeClasses[size]} />
        ) : (
          <Bookmark className={sizeClasses[size]} />
        )}
        <span className="hidden group-hover:inline text-xs">
          {isBookmarked ? 'Saved' : 'Save'}
        </span>
      </button>
    );
  }

  // Full button variant
  return (
    <button
      onClick={handleToggle}
      disabled={isLoading}
      className={`
        flex items-center gap-2 rounded-lg transition-all
        ${buttonSizeClasses[size]}
        ${
          isBookmarked
            ? 'bg-accent text-accent-foreground font-medium'
            : 'bg-cream/10 text-foreground hover:bg-cream/20 border border-border'
        }
        disabled:opacity-50
        ${className}
      `}
    >
      {isLoading ? (
        <Loader2 className={`${sizeClasses[size]} animate-spin`} />
      ) : isBookmarked ? (
        <BookmarkCheck className={sizeClasses[size]} />
      ) : (
        <Bookmark className={sizeClasses[size]} />
      )}
      <span>{isBookmarked ? 'Saved' : 'Save'}</span>
    </button>
  );
}
